import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import Input from '@components/atoms/ui-kit/input';
import { Plus, Trash2 } from 'lucide-react';
import { PricePlanTierInput } from '@utils/types/back-office/update-plan-bo';

interface PricePlanTierEditorProps {
  value: PricePlanTierInput[];
  onChange: (tiers: PricePlanTierInput[]) => void;
}

/**
 * Price plan tier editor.
 * - First tier always starts at 0 and cannot be deleted.
 * - Each non-last tier's "To" (end) is the editable boundary; the next tier's
 *   "From" (start) is auto-derived as end + 1.
 * - Last tier always has end: null (unlimited) and is not editable.
 * - No gaps or overlaps allowed.
 */
export default function PricePlanTierEditor({
  value,
  onChange
}: PricePlanTierEditorProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  /**
   * Per-tier draft for the "To" field. Keeps the raw text the user is typing
   * so mid-edit states (e.g. "0000" while deleting a leading digit) don't get
   * auto-cleaned into "0" and steal the cursor. Synced from external value
   * when the tier list changes, committed back on blur.
   */
  const [endDrafts, setEndDrafts] = useState<string[]>(() =>
    value.map(t => (t.end === null ? '' : String(t.end)))
  );

  useEffect(() => {
    setEndDrafts(value.map(t => (t.end === null ? '' : String(t.end))));
  }, [value]);

  const handleTierChangeEnd = (index: number, newValue: string) => {
    setEndDrafts(prev => {
      const next = [...prev];
      next[index] = newValue;
      return next;
    });
  };

  /**
   * Parse, clamp, and commit the draft back into the tier list.
   */
  const handleTierBlurEnd = (index: number) => {
    const draft = endDrafts[index] ?? '';
    const parsed = parseInt(draft, 10);
    const newTiers = [...value];
    const tier = newTiers[index];

    // End must be strictly greater than this tier's start.
    let end = isNaN(parsed) ? tier.start + 1 : parsed;
    if (end <= tier.start) end = tier.start + 1;

    newTiers[index] = { ...newTiers[index], end };

    // Re-glue the next tier's start.
    if (index < newTiers.length - 1) {
      newTiers[index + 1] = { ...newTiers[index + 1], start: end + 1 };
    }

    onChange(newTiers);
  };

  /**
   * Handle price per unit change
   */
  const handlePriceChange = (index: number, newValue: string) => {
    const newTiers = [...value];
    newTiers[index] = { ...newTiers[index], price_per_unit: newValue };
    onChange(newTiers);
  };

  /**
   * Add a new tier at the end
   */
  const addTier = () => {
    const newTiers = [...value];
    const last = value.at(-1);

    if (!last) {
      // First tier - starts at 0, ends at infinity
      onChange([{ start: 0, end: null, price_per_unit: '0.00' }]);
    } else {
      // Set the current last tier's end to start + 1
      const lastIndex = newTiers.length - 1;
      const newEnd = last.start + 1;
      newTiers[lastIndex] = { ...newTiers[lastIndex], end: newEnd };

      // Add new tier starting after the previous tier's end
      newTiers.push({
        start: newEnd + 1,
        end: null,
        price_per_unit: last.price_per_unit || '0.00'
      });

      onChange(newTiers);
    }
  };

  /**
   * Delete a tier (cannot delete first tier)
   */
  const dropTier = (index: number) => {
    if (index === 0) return; // Cannot delete first tier

    const newTiers = [...value];
    const next = newTiers.at(index + 1);

    // Remove the tier
    newTiers.splice(index, 1);

    if (!next) {
      // Deleted the last tier - make previous tier infinite
      newTiers[index - 1] = { ...newTiers[index - 1], end: null };
    } else {
      // Adjust previous tier's end to connect to the next tier
      newTiers[index - 1] = { ...newTiers[index - 1], end: next.start - 1 };
    }

    onChange(newTiers);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span
          className="text-xs font-medium"
          style={{ color: getColour('normalText') }}
        >
          {t('pricing-tiers')}
        </span>
        <button
          type="button"
          onClick={addTier}
          className="flex items-center gap-1 px-2 py-1 text-xs rounded-full transition-colors hover:opacity-80"
          style={{ color: getColour('activeText') }}
        >
          <Plus className="h-3 w-3" />
          {t('add-tier')}
        </button>
      </div>

      {value.length === 0 ? (
        <p
          className="text-xs py-2"
          style={{ color: getColour('inactiveText') }}
        >
          {t('no-tiers-added')}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {/* Tier cards */}
          {value.map((tier, index) => (
            <div
              key={index}
              className="p-3 rounded-2xl border border-hairline bg-app-soft"
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className="text-[9px] font-medium uppercase tracking-wide"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('tier')} {index + 1}
                </span>
                {index !== 0 && (
                  <button
                    type="button"
                    onClick={() => dropTier(index)}
                    className="p-0.5 rounded-full text-red-500 transition-colors cursor-pointer hover:opacity-80"
                  >
                    <Trash2 className="h-2.5 w-2.5" />
                  </button>
                )}
              </div>
              <div className="space-y-1">
                <div className="grid grid-cols-2 gap-1">
                  {/* From — always derived from the previous tier */}
                  <div>
                    <span
                      className="text-[9px] mb-0.5 block"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('from')}
                    </span>
                    <Input
                      type="text"
                      size="small"
                      value={tier.start}
                      onChange={() => {}}
                      disabled
                      placeholder="0"
                    />
                  </div>

                  {/* To — editable for every tier except the last (unlimited) */}
                  <div>
                    <span
                      className="text-[9px] mb-0.5 block"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('to')}
                    </span>
                    {index === value.length - 1 ? (
                      <Input
                        type="text"
                        size="small"
                        value={t('unlimited')}
                        onChange={() => {}}
                        disabled
                        placeholder={t('unlimited')}
                      />
                    ) : (
                      <Input
                        type="text"
                        inputMode="numeric"
                        size="small"
                        value={endDrafts[index] ?? ''}
                        onChange={e =>
                          handleTierChangeEnd(index, e.target.value)
                        }
                        onBlur={() => handleTierBlurEnd(index)}
                        placeholder={t('unlimited')}
                      />
                    )}
                  </div>
                </div>

                {/* Price per unit */}
                <div>
                  <span
                    className="text-[9px] mb-0.5 block"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('price-per-unit')}
                  </span>
                  <Input
                    type="text"
                    size="small"
                    value={tier.price_per_unit}
                    onChange={e => handlePriceChange(index, e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <p
        className="text-[10px] mt-1"
        style={{ color: getColour('inactiveText') }}
      >
        {t('tiers-must-start-at-zero-no-gaps')}
      </p>
    </div>
  );
}
